import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchOdds, type Odds } from '../lib/api';

/**
 * Server-authoritative odds for the current configuration.
 *
 * THE RACE THIS EXISTS TO PREVENT: a customer drags the duration from 7 to 30 to 180
 * days in a second. Three requests go out; they can come back in any order. Without
 * guarding, the slow answer for "7 days" can land last and overwrite the display with
 * odds for a configuration the customer already moved past -- showing them numbers that
 * do not match what is on screen.
 *
 * Two defences, because either alone leaves a gap:
 *
 *   1. A monotonically increasing request id. Only a response whose id is the latest one
 *      issued may touch state. A late answer is discarded, not rendered.
 *   2. An AbortController per request, so superseded requests are cancelled rather than
 *      left in flight.
 *
 * The hook is also careful never to render odds while the configuration is stale: when
 * inputs change, the previous figures are cleared, so no one sees last configuration's
 * odds sitting under this configuration's price.
 */

export type OddsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; odds: Odds }
  | { status: 'unavailable'; reason: string };

export function useOdds(params: { hashrateTh: number | null; termDays: number }): {
  state: OddsState;
  retry: () => void;
} {
  const { hashrateTh, termDays } = params;

  const [state, setState] = useState<OddsState>({ status: 'idle' });
  const [attempt, setAttempt] = useState(0);

  /** Incremented for every request; only the newest may write to state. */
  const latestRequestId = useRef(0);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (hashrateTh == null || hashrateTh <= 0) {
      setState({ status: 'idle' });
      return undefined;
    }

    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;

    const controller = new AbortController();
    setState({ status: 'loading' });

    fetchOdds({ hashrateTh, termDays }, controller.signal)
      .then((odds) => {
        // A response for a superseded configuration must never reach the screen.
        if (requestId !== latestRequestId.current) return;
        setState({ status: 'ready', odds });
      })
      .catch((error: unknown) => {
        if (requestId !== latestRequestId.current) return;
        if (controller.signal.aborted) return;

        // Odds are informational. Their absence is reported plainly and never filled in
        // with a guess.
        setState({
          status: 'unavailable',
          reason:
            (error as { message?: string })?.message ??
            'Live network difficulty is temporarily unavailable.',
        });
      });

    return () => controller.abort();
  }, [hashrateTh, termDays, attempt]);

  return { state, retry };
}
